import { formatUnits, parseUnits } from "viem";

/**
 * Wire amount (string base units) → human number (string with decimals).
 * Returns `"0"` on falsy input so the UI shows a clean zero instead of "NaN".
 */
export function fromBaseUnits(
  raw: string | undefined,
  decimals: number,
): string {
  if (raw === undefined || raw === "" || raw === "0") return "0";
  try {
    return formatUnits(BigInt(raw), decimals);
  } catch {
    return "0";
  }
}

/**
 * Human input (`"150.5"`) → wire base units (`"150500000"` for 6-dp USDC).
 * Returns `undefined` on parse error so the form can show inline validation.
 */
export function toBaseUnits(
  human: string,
  decimals: number,
): string | undefined {
  const trimmed = human.trim();
  if (trimmed === "" || trimmed === "-") return undefined;
  try {
    return parseUnits(trimmed as `${number}`, decimals).toString();
  } catch {
    return undefined;
  }
}

/**
 * Truncate a number string for display. Keeps up to `maxFractional`
 * decimal digits, drops trailing zeros. `"150.500000"` → `"150.5"`.
 * `"0.000012345"` with maxFractional=4 → `"0.0000"` (then trimmed to `"0"`).
 */
export function trimAmount(value: string, maxFractional = 6): string {
  if (!value.includes(".")) return value;
  const [whole, frac] = value.split(".") as [string, string];
  const truncated = frac.slice(0, maxFractional).replace(/0+$/, "");
  return truncated.length === 0 ? whole : `${whole}.${truncated}`;
}

/**
 * Format a base-units amount with token context (symbol, decimals) into
 * a single user-facing string. `"150500000"` + USDC → `"150.5 USDC"`.
 */
export function formatTokenAmount(
  rawBaseUnits: string | undefined,
  token: { readonly symbol: string; readonly decimals: number },
  maxFractional = 6,
): string {
  const human = fromBaseUnits(rawBaseUnits, token.decimals);
  return `${trimAmount(human, maxFractional)} ${token.symbol}`;
}

/**
 * Build a block explorer URL for a tx hash + chainId. Used to render
 * "view on Polygonscan" links from `quote.steps[*].tx`.
 */
const EXPLORER_TX_BASE: Readonly<Record<number, string>> = {
  1: "https://etherscan.io/tx/",
  10: "https://optimistic.etherscan.io/tx/",
  56: "https://bscscan.com/tx/",
  137: "https://polygonscan.com/tx/",
  8453: "https://basescan.org/tx/",
  42161: "https://arbiscan.io/tx/",
};

export function explorerTxUrl(
  chainId: number,
  txHash: string,
): string | undefined {
  const base = EXPLORER_TX_BASE[chainId];
  return base !== undefined ? `${base}${txHash}` : undefined;
}
